import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  GESTIFY_TREASURY_PATHS,
  gestifyDue,
  gestifyInvoice,
  gestifyInvoiceDetails,
  gestifyItemPath,
  gestifyLinesPath,
  type ImportedInvoice,
  type InvoiceImportResult,
  invoiceKey,
  fromCents,
  toCents,
  TREASURY_START_DATE,
} from '@k-management/shared';
import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { GestifyConnectDialog } from '../../components/GestifyConnectDialog.js';
import { apiRequest } from '../../lib/api.js';
import { formatDate } from '../../lib/format.js';
import { useGestifyBrowser } from '../../lib/gestify-browser.js';
import { fetchProposals, pendingSubjects, RegistryCheckDialog } from './RegistryCheckDialog.js';
import { proposalKeys } from './RegistryProposalsPage.js';
import { treasuryKeys, worklistKeys } from './treasury-ui.js';

interface Reading {
  invoices: ImportedInvoice[];
  /** Documents of Gestify that are not invoices to collect or pay, by kind. */
  excluded: [string, number][];
  /** Records that could not be read: how many, why, and the names of the fields of the first. */
  unread: { count: number; reasons: string[]; fields: string[] };
  /** Due dates that could not be read: how many, and the names of the fields of the first. */
  duesUnread: { count: number; fields: string[] };
}

/** Details read at the same time: enough to be quick, few enough for Gestify. */
const PARALLEL = 4;

/**
 * «Aggiorna da Gestify» (06/10/2026): this browser reads the fiscal documents
 * and the scadenzario from 01/01/2025 (ADR-017) and the API shows what would
 * change. On «Importa» it reads the detail and the lines of the new and
 * changed invoices, one call each, then imports. Gestify stays the source of
 * the amounts; what K-Management adds is never overwritten.
 */
export function GestifyInvoicesDialog({ onClose }: { onClose: () => void }) {
  const navigate = useNavigate();
  const gestify = useGestifyBrowser();
  const queryClient = useQueryClient();
  const dialogRef = useRef<HTMLDialogElement>(null);
  const [connecting, setConnecting] = useState(!gestify.connectedAs);
  const signedIn = useRef(Boolean(gestify.connectedAs));
  const [progress, setProgress] = useState<string | null>(null);
  const [reading, setReading] = useState<Reading | null>(null);
  const [result, setResult] = useState<InvoiceImportResult | null>(null);
  const [detailErrors, setDetailErrors] = useState(0);
  // Invoices left without subject to decide about: the window of the Anagrafica check (1.78).
  const [checking, setChecking] = useState(false);

  // First the sign-in to Gestify, alone; then this dialog.
  useEffect(() => {
    if (!connecting && !dialogRef.current?.open) dialogRef.current?.showModal();
  }, [connecting]);

  const send = (invoices: ImportedInvoice[], dryRun: boolean) =>
    apiRequest<InvoiceImportResult>('/treasury/invoices/import', {
      method: 'POST',
      body: { invoices, dryRun },
    });

  const read = useMutation({
    mutationFn: async (): Promise<{ reading: Reading; preview: InvoiceImportResult }> => {
      const records = (path: string, what: string) =>
        gestify.readAll(path, (done, total) =>
          setProgress(`${what}: ${done}${total !== null ? ` di ${total}` : ''}…`),
        );
      // Each list answers with sales and purchases together: merged by id.
      const documents = new Map<string, Record<string, unknown>>();
      for (const record of [
        ...(await records(GESTIFY_TREASURY_PATHS.sales, 'Documenti di vendita')),
        ...(await records(GESTIFY_TREASURY_PATHS.purchases, 'Documenti di acquisto')),
      ]) {
        documents.set(String(record.id), record);
      }
      const dueRecords = new Map<string, Record<string, unknown>>();
      for (const record of [
        ...(await records(GESTIFY_TREASURY_PATHS.dueIn, 'Scadenze in entrata')),
        ...(await records(GESTIFY_TREASURY_PATHS.dueOut, 'Scadenze in uscita')),
      ]) {
        dueRecords.set(String(record.id ?? `${dueRecords.size}`), record);
      }
      const duesUnread = { count: 0, fields: [] as string[] };
      const duesOf = new Map<string, ImportedInvoice['dues']>();
      // The withholding tax is not an instalment: its total, by invoice.
      const withheldOf = new Map<string, number>();
      for (const record of dueRecords.values()) {
        const due = gestifyDue(record);
        if (!due) {
          duesUnread.count += 1;
          if (!duesUnread.fields.length) duesUnread.fields = Object.keys(record).sort();
          continue;
        }
        if (due.withholding) {
          withheldOf.set(
            due.invoiceId,
            (withheldOf.get(due.invoiceId) ?? 0) + Math.abs(toCents(due.amount)),
          );
          continue;
        }
        const group = duesOf.get(due.invoiceId) ?? [];
        group.push({ dueDate: due.dueDate, amount: due.amount });
        duesOf.set(due.invoiceId, group);
      }
      const unread = { count: 0, reasons: new Set<string>(), fields: [] as string[] };
      const excluded = new Map<string, number>();
      const invoices: ImportedInvoice[] = [];
      for (const record of documents.values()) {
        const one = gestifyInvoice(record);
        if (one.ok) {
          const withheld = withheldOf.get(one.invoice.gestifyId);
          invoices.push({
            ...one.invoice,
            dues: duesOf.get(one.invoice.gestifyId) ?? [],
            ...(withheld !== undefined && {
              withholding: fromCents(one.invoice.kind === 'nota_credito' ? -withheld : withheld),
            }),
          });
        } else if ('excluded' in one) {
          excluded.set(one.excluded, (excluded.get(one.excluded) ?? 0) + 1);
        } else {
          unread.count += 1;
          unread.reasons.add(one.reason);
          if (!unread.fields.length) unread.fields = one.fields;
        }
      }
      setProgress('Confronto con K-Management…');
      const preview = await send(invoices, true);
      return {
        reading: {
          invoices,
          excluded: [...excluded.entries()].sort((a, b) => b[1] - a[1]),
          unread: { count: unread.count, reasons: [...unread.reasons], fields: unread.fields },
          duesUnread,
        },
        preview,
      };
    },
    onSuccess: (data) => {
      setProgress(null);
      setReading(data.reading);
      setResult(data.preview);
    },
    onError: () => setProgress(null),
  });

  const run = useMutation({
    mutationFn: async () => {
      const wanted = new Set(result!.needDetails);
      const targets = reading!.invoices.filter((invoice) => wanted.has(invoiceKey(invoice)));
      const withDetails = new Map<string, ImportedInvoice>();
      let done = 0;
      let failed = 0;
      const queue = [...targets];
      const worker = async () => {
        for (let invoice = queue.shift(); invoice; invoice = queue.shift()) {
          try {
            const [item, lines] = await Promise.all([
              gestify.read(gestifyItemPath(invoice.direction, invoice.gestifyId)),
              gestify.readAll(gestifyLinesPath(invoice.direction, invoice.gestifyId)),
            ]);
            withDetails.set(invoiceKey(invoice), {
              ...invoice,
              details: gestifyInvoiceDetails(item, lines, invoice),
            });
          } catch {
            failed += 1;
          }
          done += 1;
          setProgress(`Dettagli e righe: ${done} di ${targets.length}…`);
        }
      };
      await Promise.all(Array.from({ length: Math.min(PARALLEL, targets.length) }, worker));
      setDetailErrors(failed);
      setProgress('Importazione…');
      return send(
        reading!.invoices.map((invoice) => withDetails.get(invoiceKey(invoice)) ?? invoice),
        false,
      );
    },
    onSuccess: async (data) => {
      setProgress(null);
      setResult(data);
      await queryClient.invalidateQueries({ queryKey: treasuryKeys.all });
      await queryClient.invalidateQueries({ queryKey: worklistKeys.all });
      await queryClient.invalidateQueries({ queryKey: proposalKeys });
      const proposals = await queryClient.fetchQuery({
        queryKey: proposalKeys,
        queryFn: fetchProposals,
      });
      if (pendingSubjects(proposals) > 0) setChecking(true);
    },
    onError: () => setProgress(null),
  });

  // Connected (now or before): read at once.
  const started = useRef(false);
  useEffect(() => {
    if (!connecting && gestify.connectedAs && !started.current) {
      started.current = true;
      read.mutate();
    }
  }, [connecting, gestify.connectedAs, read]);

  if (connecting) {
    return (
      <GestifyConnectDialog
        onClose={() => {
          setConnecting(false);
          // Closed without signing in: nothing to read.
          if (!signedIn.current) onClose();
        }}
        onConnected={() => {
          signedIn.current = true;
          setConnecting(false);
        }}
      />
    );
  }

  if (checking && result) return <RegistryCheckDialog result={result} onClose={onClose} />;

  const done = result && !result.dryRun;
  const work = result
    ? result.created + result.changed + result.removed + result.needDetails.length
    : 0;
  const busy = read.isPending || run.isPending;

  return (
    <dialog ref={dialogRef} className="dialog narrow" onClose={onClose}>
      <div className="stack">
        <h2>Aggiorna le fatture da Gestify</h2>
        {gestify.connectedAs && (
          <p className="muted small">
            Collegato a Gestify come <strong>{gestify.connectedAs}</strong>. Legge i documenti
            fiscali e lo scadenzario dal {formatDate(TREASURY_START_DATE)}; tiene solo fatture, note
            di credito e note di debito.
          </p>
        )}
        {progress && <p className="muted">{progress}</p>}
        {read.error && <p className="error">{read.error.message}</p>}
        {reading && result && (
          <dl className="totals">
            <div>
              <dt>Fatture nuove</dt>
              <dd>{result.created}</dd>
            </div>
            <div>
              <dt>Fatture cambiate in Gestify</dt>
              <dd>{result.changed}</dd>
            </div>
            <div>
              <dt>Invariate</dt>
              <dd>{result.unchanged}</dd>
            </div>
            <div>
              <dt>{done ? 'Tolte' : 'Da togliere'} (doppioni o non più fatture)</dt>
              <dd>{result.removed}</dd>
            </div>
            {result.kept > 0 && (
              <div>
                <dt>Non più in Gestify, tenute perché hanno incassi o note</dt>
                <dd>{result.kept}</dd>
              </div>
            )}
            {!done && (
              <div>
                <dt>Dettagli e righe da leggere</dt>
                <dd>{result.needDetails.length}</dd>
              </div>
            )}
            <div>
              <dt>Soggetti da collegare o da creare in Anagrafica</dt>
              <dd>{result.unmatched.length}</dd>
            </div>
          </dl>
        )}
        {reading && reading.excluded.length > 0 && (
          <p className="muted small">
            Documenti esclusi, perché non sono fatture da incassare o pagare:{' '}
            {reading.excluded.map(([label, count]) => `${count} ${label}`).join(', ')}.
          </p>
        )}
        {!done && result && result.needDetails.length > 100 && (
          <p className="muted small">
            Dettagli e righe si leggono una fattura alla volta: per {result.needDetails.length}{' '}
            fatture servono alcuni minuti. Le volte successive solo quelle nuove o cambiate.
          </p>
        )}
        {done && <ProposalsNotice />}
        {result && result.unmatched.length > 0 && (
          <details>
            <summary className="small">Soggetti da abbinare</summary>
            <p className="muted small">
              Le loro fatture si importano comunque, con il nome di Gestify; si collegano da sole
              quando il soggetto è in Anagrafica con la stessa partita IVA o codice fiscale e lo
              stesso nome (o un suo alias). Con un altro nome si decide nel controllo Anagrafica.
            </p>
            <ul className="small">
              {result.unmatched.map((name) => (
                <li key={name}>{name}</li>
              ))}
            </ul>
          </details>
        )}
        {reading && reading.unread.count > 0 && (
          <p className="notice small">
            {reading.unread.count === 1
              ? '1 documento di Gestify non letto'
              : `${reading.unread.count} documenti di Gestify non letti`}{' '}
            ({reading.unread.reasons.join(', ')}). Campi del primo, da comunicare per correggere la
            lettura: {reading.unread.fields.join(', ')}.
          </p>
        )}
        {reading && reading.duesUnread.count > 0 && (
          <p className="notice small">
            {reading.duesUnread.count} scadenze dello scadenzario non lette. Campi della prima, da
            comunicare per correggere la lettura: {reading.duesUnread.fields.join(', ')}.
          </p>
        )}
        {done && (
          <p className="notice info">
            Fatto: {result.created} nuove, {result.changed} aggiornate, {result.removed} tolte.
            Incassi, pagamenti, conti e note già segnati non sono cambiati.
            {detailErrors > 0 &&
              ` Per ${detailErrors} fatture Gestify non ha dato dettaglio e righe: si rileggono al prossimo aggiornamento.`}
          </p>
        )}
        {run.error && <p className="error">{run.error.message}</p>}
        <div className="actions">
          <button type="button" onClick={onClose} disabled={run.isPending}>
            {done ? 'Chiudi' : 'Annulla'}
          </button>
          {done && result.readId && (
            <button
              type="button"
              className="primary"
              onClick={() => {
                onClose();
                void navigate(
                  `/tesoreria/da-sistemare?lotto=${encodeURIComponent(`gestify:${result.readId}`)}`,
                );
              }}
            >
              Inizia ora
            </button>
          )}
          {!done && reading && (
            <button
              type="button"
              className="primary"
              disabled={work === 0 || busy}
              onClick={() => run.mutate()}
            >
              {run.isPending ? 'Attendere…' : work === 0 ? 'Già aggiornate' : 'Importa'}
            </button>
          )}
        </div>
      </div>
    </dialog>
  );
}

/** After a reading (1.73): what the invoices now propose to the Anagrafica. */
function ProposalsNotice() {
  const count = useQuery({
    queryKey: ['treasury', 'registry-proposals', 'count'],
    queryFn: () => apiRequest<{ count: number }>('/treasury/registry-proposals/count'),
  });
  if (!count.data?.count) return null;
  return (
    <p className="notice small">
      {count.data.count === 1
        ? '1 proposta per l’Anagrafica'
        : `${count.data.count} proposte per l’Anagrafica`}{' '}
      (soggetti, ruoli, dati, alias):{' '}
      <Link to="/tesoreria/anagrafica-dalle-fatture">apri «Anagrafica dalle fatture»</Link>.
    </p>
  );
}
